/**
 * Progress Tab
 *
 * Displays the operator's performance journey: summary stats, skill radar,
 * achievements, and timeline.
 *
 * Summary cards show month-over-month stats:
 * - Score trend (+12%, "First month", or "--")
 * - Assessments completed (count)
 * - New badges earned (count)
 *
 * Timeline shows assessment, training, and achievement events with
 * Load More pagination that appends items to an accumulated list.
 *
 * Data source: useOperatorProgress() from /api/operator/progress
 */

import { useState, useCallback, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, ClipboardList, Award, RefreshCw, Loader2 } from 'lucide-react';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { useOperatorProgress } from './hooks/useOperatorProgress';
import type { TimelineEvent } from './hooks/useOperatorProgress';
import { OperatorCard } from './components/OperatorCard';
import { OperatorEmptyState } from './components/OperatorEmptyState';
import { OperatorLoadingSkeleton } from './components/OperatorLoadingSkeleton';
import { SkillRadarChart } from './components/SkillRadarChart';
import { AchievementBadge } from './components/AchievementBadge';
import { TimelineItem } from './components/TimelineItem';
import { AssessmentDetailSheet } from './components/AssessmentDetailSheet';

export function ProgressTab() {
  useDocumentTitle('My Progress');

  // Pagination state: track current offset and accumulated timeline items
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [accumulatedTimeline, setAccumulatedTimeline] = useState<TimelineEvent[]>([]);
  const [hasMore, setHasMore] = useState(false);

  // Assessment detail sheet state
  const [selectedAssessment, setSelectedAssessment] = useState<TimelineEvent | null>(null);

  const progress = useOperatorProgress(timelineOffset);

  // Accumulate timeline items as pages are loaded
  useEffect(() => {
    if (!progress.data) return;

    const newItems = progress.data.timeline;
    const pagination = progress.data.pagination;

    if (pagination.offset === 0) {
      // First page: replace all
      setAccumulatedTimeline(newItems);
    } else {
      // Subsequent pages: append new items, deduplicate by id
      setAccumulatedTimeline((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const uniqueNew = newItems.filter((item) => !existingIds.has(item.id));
        return [...prev, ...uniqueNew];
      });
    }

    setHasMore(pagination.hasMore);
  }, [progress.data]);

  const handleLoadMore = useCallback(() => {
    if (progress.isFetching) return;
    setTimelineOffset((prev) => prev + (progress.data?.pagination.limit ?? 20));
  }, [progress.isFetching, progress.data?.pagination.limit]);

  // Loading state (only for initial load)
  if (progress.isLoading && timelineOffset === 0) {
    return <OperatorLoadingSkeleton variant="page" />;
  }

  // Error state
  if (progress.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
          <RefreshCw size={24} strokeWidth={1.5} className="text-red-400" />
        </div>
        <p className="mt-4 text-base font-semibold text-op-text-primary">
          Could not load progress
        </p>
        <p className="mt-1 text-sm text-op-text-secondary">
          Please check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => progress.refetch()}
          className="mt-4 min-h-[44px] rounded-xl bg-guild-green px-6 py-2 text-sm font-semibold text-white active:bg-guild-green/90 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  const data = progress.data;

  // Empty state: no assessments and no training
  if (
    data &&
    data.summary.assessmentCount === 0 &&
    data.summary.prevAssessmentCount === 0 &&
    data.summary.trainingCompleted === 0 &&
    data.achievements.length === 0 &&
    data.timeline.length === 0
  ) {
    return (
      <OperatorEmptyState
        icon={TrendingUp}
        title="No Progress Yet"
        description="Complete your first assessment or training to start seeing your progress here."
      />
    );
  }

  if (!data) return null;

  return (
    <>
    <div className="space-y-6">
      {/* ============================================ */}
      {/* SUMMARY CARDS                               */}
      {/* ============================================ */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-op-text-primary">This Month</h2>
        <div className="grid grid-cols-3 gap-3">
          {/* Score Trend Card */}
          <div style={{ animation: 'fadeInUp 0.4s ease-out 0s both' }}>
            <OperatorCard>
              <div className="flex flex-col items-center text-center">
                <div className="mb-1.5">
                  <ScoreTrendIcon
                    trend={data.summary.scoreTrend}
                    scoreChange={data.summary.scoreChange}
                  />
                </div>
                <span className="text-lg font-bold text-op-text-primary">
                  <ScoreTrendValue
                    trend={data.summary.scoreTrend}
                    scoreChange={data.summary.scoreChange}
                    assessmentCount={data.summary.assessmentCount}
                    prevAssessmentCount={data.summary.prevAssessmentCount}
                  />
                </span>
                <span className="mt-0.5 text-[11px] font-medium text-op-text-tertiary">
                  Score Trend
                </span>
              </div>
            </OperatorCard>
          </div>

          {/* Assessments Card */}
          <div style={{ animation: 'fadeInUp 0.4s ease-out 0.06s both' }}>
            <OperatorCard>
              <div className="flex flex-col items-center text-center">
                <div className="mb-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                  <ClipboardList size={16} strokeWidth={1.5} />
                </div>
                <span className="text-lg font-bold text-op-text-primary">
                  {data.summary.assessmentCount}
                </span>
                <span className="mt-0.5 text-[11px] font-medium text-op-text-tertiary">
                  Assessments
                </span>
              </div>
            </OperatorCard>
          </div>

          {/* Badges Card */}
          <div style={{ animation: 'fadeInUp 0.4s ease-out 0.12s both' }}>
            <OperatorCard>
              <div className="flex flex-col items-center text-center">
                <div className="mb-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <Award size={16} strokeWidth={1.5} />
                </div>
                <span className="text-lg font-bold text-op-text-primary">
                  {data.summary.newBadges}
                </span>
                <span className="mt-0.5 text-[11px] font-medium text-op-text-tertiary">
                  New Badges
                </span>
              </div>
            </OperatorCard>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SKILL RADAR                                */}
      {/* ============================================ */}
      {data.radar.current.compliance + data.radar.current.efficiency + data.radar.current.safety > 0 && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out 0.18s both' }}>
          <h2 className="mb-3 text-lg font-semibold text-op-text-primary">Skills</h2>
          <OperatorCard>
            <SkillRadarChart
              current={data.radar.current}
              previous={data.radar.previous}
            />
          </OperatorCard>
        </div>
      )}

      {/* ============================================ */}
      {/* ACHIEVEMENTS                                */}
      {/* ============================================ */}
      {data.achievements.length > 0 && (
        <div style={{ animation: 'fadeInUp 0.4s ease-out 0.24s both' }}>
          <h2 className="mb-3 text-lg font-semibold text-op-text-primary">
            Achievements{' '}
            <span className="text-sm font-normal text-op-text-tertiary">
              ({data.achievements.length})
            </span>
          </h2>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
            {data.achievements.map((achievement, i) => (
              <div
                key={achievement.type + achievement.earnedAt + (achievement.goalTitle ?? '')}
                style={{
                  animation: `fadeInUp 0.4s ease-out ${0.24 + (i + 1) * 0.06}s both`,
                }}
              >
                <AchievementBadge achievement={achievement} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* TIMELINE                                    */}
      {/* ============================================ */}
      <div style={{ animation: 'fadeInUp 0.4s ease-out 0.30s both' }}>
        <h2 className="mb-3 text-lg font-semibold text-op-text-primary">Timeline</h2>

        {accumulatedTimeline.length > 0 ? (
          <>
            <div>
              {accumulatedTimeline.map((item, i) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  isFirst={i === 0}
                  isLast={i === accumulatedTimeline.length - 1 && !hasMore}
                  onSelect={item.type === 'assessment' ? setSelectedAssessment : undefined}
                />
              ))}
            </div>

            {/* Load More button */}
            {hasMore && (
              <div className="mt-2 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={progress.isFetching}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-op-text-secondary transition-colors duration-150 active:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
                >
                  {progress.isFetching ? (
                    <>
                      <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty timeline message */
          <div className="rounded-2xl bg-gray-50 px-4 py-6 text-center">
            <p className="text-sm text-op-text-secondary">
              No activity yet. Your assessments and training completions will appear here.
            </p>
          </div>
        )}
      </div>
    </div>

    {/* Assessment Detail Sheet (Phase 21.4.6) */}
    <AssessmentDetailSheet
      assessment={selectedAssessment}
      isOpen={selectedAssessment !== null}
      onClose={() => setSelectedAssessment(null)}
    />
    </>
  );
}

// ============================================
// SCORE TREND HELPERS
// ============================================

/** Icon for the score trend card */
function ScoreTrendIcon({
  trend,
  scoreChange,
}: {
  trend: 'improving' | 'steady' | 'declining' | null;
  scoreChange: number | null;
}) {
  if (trend === 'improving' && scoreChange !== null && scoreChange > 0) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
        <TrendingUp size={16} strokeWidth={1.5} />
      </div>
    );
  }

  if (trend === 'declining' && scoreChange !== null && scoreChange < 0) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500">
        <TrendingDown size={16} strokeWidth={1.5} />
      </div>
    );
  }

  if (trend === 'steady') {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-op-text-tertiary">
        <Minus size={16} strokeWidth={1.5} />
      </div>
    );
  }

  // null trend (no data or first month)
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-op-text-tertiary">
      <TrendingUp size={16} strokeWidth={1.5} />
    </div>
  );
}

/** Text value for the score trend card */
function ScoreTrendValue({
  trend,
  scoreChange,
  assessmentCount,
  prevAssessmentCount,
}: {
  trend: 'improving' | 'steady' | 'declining' | null;
  scoreChange: number | null;
  assessmentCount: number;
  prevAssessmentCount: number;
}) {
  // No assessments at all
  if (assessmentCount === 0 && prevAssessmentCount === 0) {
    return <span className="text-op-text-tertiary">--</span>;
  }

  // First month (has current assessments but no previous month data)
  if (assessmentCount > 0 && prevAssessmentCount === 0 && scoreChange === null) {
    return <span className="text-sm text-op-text-secondary">First month</span>;
  }

  // Has score change data
  if (scoreChange !== null) {
    const sign = scoreChange > 0 ? '+' : '';
    const colorClass =
      trend === 'improving'
        ? 'text-emerald-600'
        : trend === 'declining'
          ? 'text-red-500'
          : 'text-op-text-secondary';

    return <span className={colorClass}>{sign}{Math.round(scoreChange)}%</span>;
  }

  // Fallback
  return <span className="text-op-text-tertiary">--</span>;
}
